import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FileText, Plus, RefreshCw, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { fmtRelative } from "@/lib/format";
import { apiErrorMessage } from "@/lib/apiError";

const TYPE_LABEL = { msa: "MSA", sow: "SOW", change_order: "Change Order" };
const TYPE_STYLE = {
  msa: "bg-primary/10 text-primary border-primary/30",
  sow: "bg-emerald-50 text-emerald-700 border-emerald-300",
  change_order: "bg-amber-50 text-amber-700 border-amber-300",
};
const STATUS_STYLE = {
  draft: "bg-slate-100 text-slate-700 border-slate-300",
  sent: "bg-sky-100 text-sky-700 border-sky-300",
  approved: "bg-emerald-100 text-emerald-700 border-emerald-300",
  signed: "bg-primary/10 text-primary border-primary/30",
  rejected: "bg-rose-100 text-rose-700 border-rose-300",
  cancelled: "bg-rose-100 text-rose-700 border-rose-300",
  expired: "bg-slate-100 text-slate-500 border-slate-300",
};

function fmtMoney(v, currency = "INR") {
  if (v === null || v === undefined) return "—";
  return `${currency === "INR" ? "₹" : currency + " "}${Number(v).toLocaleString("en-IN")}`;
}

export default function ContractList() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState("all");
  const [showNew, setShowNew] = useState(false);
  const nav = useNavigate();

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const params = typeFilter === "all" ? {} : { contract_type: typeFilter };
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/contracts`, { params });
      setRows(data || []);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, typeFilter]);

  return (
    <div className="p-6 space-y-5" data-testid="contract-list-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground flex items-center gap-1.5">
            <FileText className="w-3 h-3" /> Estimates & Contracts
          </div>
          <h1 className="text-3xl font-bold mt-0.5 tracking-tight">Contracts</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Master Service Agreements, Statements of Work, and Change Orders — with versioning and side-by-side compare.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="text-xs px-3 py-1.5 border border-border hover:bg-muted flex items-center gap-1.5" data-testid="contract-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button type="button" onClick={() => setShowNew(true)} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 flex items-center gap-1.5 font-medium" data-testid="contract-new-btn">
            <Plus className="w-3.5 h-3.5" /> New Contract
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        {["all", "msa", "sow", "change_order"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTypeFilter(t)}
            className={`text-xs px-3 py-1.5 border transition ${typeFilter === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`}
            data-testid={`contract-filter-${t}`}
          >
            {t === "all" ? "All" : TYPE_LABEL[t]}
          </button>
        ))}
      </div>

      <div className="border border-border overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase text-muted-foreground tracking-wide">
              <th className="px-3 py-2">Number</th>
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Title</th>
              <th className="px-3 py-2">Client / Brand</th>
              <th className="px-3 py-2">Version</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {rows.length === 0 && !loading && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-muted-foreground">No contracts yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.id} className="hover:bg-muted/30 cursor-pointer" onClick={() => nav(`/app/contracts/${r.id}`)} data-testid={`contract-row-${r.id}`}>
                <td className="px-3 py-2 font-mono text-[11px]">{r.number}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] ${TYPE_STYLE[r.contract_type]}`}>{TYPE_LABEL[r.contract_type]}</span>
                </td>
                <td className="px-3 py-2 font-medium">{r.title}</td>
                <td className="px-3 py-2 text-muted-foreground">
                  {r.client_name || "—"} {r.brand_name && <span className="text-[10px]">/ {r.brand_name}</span>}
                </td>
                <td className="px-3 py-2">v{r.version || 1}</td>
                <td className="px-3 py-2">
                  <span className={`inline-block px-2 py-0.5 border text-[10px] ${STATUS_STYLE[r.status] || STATUS_STYLE.draft}`}>{r.status}</span>
                </td>
                <td className="px-3 py-2 text-muted-foreground">{fmtRelative(r.created_at)}</td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/app/contracts/${r.id}`} className="text-[10px] text-primary hover:underline inline-flex items-center gap-0.5">
                    Open <ArrowRight className="w-3 h-3" />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showNew && <NewContractDialog wsId={activeWorkspaceId} onClose={() => setShowNew(false)} onCreated={(c) => { setShowNew(false); nav(`/app/contracts/${c.id}`); }} />}
    </div>
  );
}

function NewContractDialog({ wsId, onClose, onCreated }) {
  const [type, setType] = useState("msa");
  const [title, setTitle] = useState("");
  const [parentId, setParentId] = useState("");
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [parents, setParents] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/clients`).then(({ data }) => setClients(data || []));
  }, [wsId]);

  useEffect(() => {
    if (!wsId || type === "msa") { setParents([]); return; }
    // SOW parents = MSA; CR parents = SOW or MSA
    const t = type === "sow" ? "msa" : null; // for change_order pull all msa+sow via 2 calls
    if (t) {
      api.get(`/workspaces/${wsId}/contracts?contract_type=${t}`).then(({ data }) => setParents(data || []));
    } else {
      Promise.all([
        api.get(`/workspaces/${wsId}/contracts?contract_type=msa`),
        api.get(`/workspaces/${wsId}/contracts?contract_type=sow`),
      ]).then(([a, b]) => setParents([...(a.data || []), ...(b.data || [])]));
    }
  }, [wsId, type]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/contracts`, {
        contract_type: type,
        title: title.trim(),
        parent_contract_id: parentId || undefined,
        client_id: clientId || undefined,
      });
      toast.success(`Created ${data.number}`);
      onCreated(data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed"));
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[1100] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="contract-new-dialog">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-background border border-border shadow-2xl">
        <div className="px-4 py-3 border-b border-border bg-muted/40 font-semibold text-sm">New Contract</div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Type</label>
            <div className="flex gap-1 mt-1">
              {["msa", "sow", "change_order"].map((t) => (
                <button key={t} type="button" onClick={() => { setType(t); setParentId(""); }} className={`text-xs px-3 py-1.5 border ${type === t ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-muted"}`} data-testid={`contract-new-type-${t}`}>
                  {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Title</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} className="w-full text-sm px-2 py-1.5 mt-0.5 bg-background border border-border focus:outline-none focus:border-primary" data-testid="contract-new-title" />
          </div>
          <div>
            <label className="text-[10px] uppercase text-muted-foreground">Client</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border">
              <option value="">—</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          {type !== "msa" && (
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Parent contract</label>
              <select value={parentId} onChange={(e) => setParentId(e.target.value)} className="w-full text-xs px-2 py-1.5 mt-0.5 bg-background border border-border" data-testid="contract-new-parent">
                <option value="">—</option>
                {parents.map((p) => <option key={p.id} value={p.id}>{p.number} · {p.title}</option>)}
              </select>
            </div>
          )}
        </div>
        <div className="px-4 py-3 border-t border-border bg-muted/20 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 border border-border hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="text-xs px-3 py-1.5 bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 font-medium" data-testid="contract-new-save">
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

export { TYPE_LABEL, TYPE_STYLE, STATUS_STYLE, fmtMoney };
